import { MetadataModeService } from 'app/services/metadata-mode.service';
import { convertObjectToString, format } from 'app/shared/utils/string.util';
import AdfDefaultTemplate from 'assets/json/arm-template-parameters-definition.json';
import A365DefaultTemplate from 'assets/json/a365-arm-template-parameters-definition.json';
import { IFileClient } from 'app/services/interfaces/IFileClient';
import { BaseFileClientProviderService } from '../base-file-client-provider.service';
import { LoggerService } from 'app/logger/logger.service';
import { IArmTemplate, IArmTemplateResource, parameterSyntax } from 'app/interfaces/IArmTemplate';
import * as Constants from 'app/services/globals';
import { irTypesConst } from 'app/integrationruntime/integrationruntime.constants';
import { LinkedService, ModelConstants } from 'shell-models-lib';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export const workspaceNameParam = 'workspaceName';
export const workspaceIdVariable = 'workspaceId';
export const factoryNameParam = 'factoryName';
export const factoryIdVariable = 'factoryId';

interface ISelectiveDeploymentOption extends ISelectOption<string> {
    supportsFolderExclusion: boolean;
}

export class ArmTemplateUtils {
    public static readonly selectiveDeploymentResourceTypes: ISelectiveDeploymentOption[] = [
        { label: stringResources.Trigger, value: 'trigger', supportsFolderExclusion: false  },
        { label: stringResources.ModelName_Credential, value: ModelConstants.credential, supportsFolderExclusion: false },
        { label: stringResources.ManagedPrivateEndpoint, value: ModelConstants.managedPrivateEndpoint, supportsFolderExclusion: false },
        { label: stringResources.Pipeline, value: 'pipeline', supportsFolderExclusion: true }
    ];

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static readonly armTemplateParametersFilePath = __IS_A365__
        ? 'template-parameters-definition.json'
        : 'arm-template-parameters-definition.json';

    private static readonly className: string = 'ArmTemplateUtils';
    private static readonly irType = Constants.FACTORY_FULLRESOURCE_TYPE + '/integrationRuntimes';

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static readonly nameParam: string = __IS_A365__ ? workspaceNameParam : factoryNameParam;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static readonly idVariable: string = __IS_A365__ ? workspaceIdVariable : factoryIdVariable;

    public static async getArmTemplateParameterDefinition(contextData: IResourceContext, useDefaultIfInvalid: boolean = true): Promise<object> {
        const loggerService = await getServiceInstance<LoggerService>(contextData, 'LoggerService');
        let definitions: object;
        const parameterDefinitionJson = await ArmTemplateUtils.getArmTemplateParameterDefinitionJson(contextData);

        try {
            definitions = JSON.parse(parameterDefinitionJson);
        } catch (err) {
            loggerService.error(this.className, `Invalid parameter definition, error: ${convertObjectToString(err)}`);
            if (useDefaultIfInvalid) {
                const defaultDefinition = await this._getDefaultParameterDefinitionJson();
                return JSON.parse(defaultDefinition);
            }
            throw err;
        }
        return definitions;
    }

    public static async hasCustomParameterDefinition(contextData: IResourceContext): Promise<boolean> {
        return !!(await ArmTemplateUtils._getUserParameterDefinitionJson(contextData));
    }

    public static async getArmTemplateParameterDefinitionJson(contextData: IResourceContext): Promise<string> {
        return await ArmTemplateUtils._getUserParameterDefinitionJson(contextData) || await this._getDefaultParameterDefinitionJson();
    }

    public static excludeSsisIRs(fullArmTemplate: IArmTemplate): IArmTemplate {
        if (fullArmTemplate && fullArmTemplate.resources) {
            fullArmTemplate.resources = fullArmTemplate.resources.filter((templateResource) => this.getIRTypeFromArmTemplateResource(templateResource) !== irTypesConst.AzureSSIS);
        }
        return fullArmTemplate;
    }

    public static getIRTypeFromArmTemplateResource(armTemplateResource: IArmTemplateResource): string {
        if (armTemplateResource && armTemplateResource.type === ArmTemplateUtils.irType) {
            const properties = armTemplateResource.properties;
            if (properties != null && properties.type === irTypesConst.Managed) {
                if (properties.typeProperties != null && properties.typeProperties.ssisProperties != null) {
                    return irTypesConst.AzureSSIS;
                }
            }
            // Add other types check here if needed
        }
        return undefined;
    }

    public static parameterizeLinkedServiceReference(contextData: IResourceContext, resource: IArmTemplateResource, template: IArmTemplate, linkedServices: LinkedService[],
        referencedLinkedServiceSet: Set<LinkedService>): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const property in resource) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value = (resource as any)[property];
            if (value != null && typeof value === 'object') {
                this.replacePropertyToParameter(contextData, value, template, linkedServices, referencedLinkedServiceSet, property);
            } else if (Array.isArray(value) && value.length > 0) {
                value.forEach(() => {
                    this.replacePropertyToParameter(contextData, value, template, linkedServices, referencedLinkedServiceSet, property);
                });
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static replacePropertyToParameter(contextData: IResourceContext, value: any, template: IArmTemplate, linkedServices: LinkedService[], referencedLinkedServiceSet: Set<LinkedService>, propertyName?: string): void {
        if (value != null && typeof value === 'object') {
            if (value.referenceName != null && value.type === 'LinkedServiceReference') {
                value.referenceName = this.generateParameter(value.referenceName, template, linkedServices, referencedLinkedServiceSet);
            } else if (__IS_FABRIC_DPE__&& (propertyName === 'linkedService' || propertyName === 'connectionSettings') && value.properties?.type) {
                if (value.properties.typeProperties?.database != null) {
                    value.properties.typeProperties.database = this.generateParameter(value.properties.typeProperties.database, template, linkedServices, referencedLinkedServiceSet);
                } else if (value.properties.typeProperties?.artifactId) {
                    value.properties.typeProperties.artifactId = this.generateParameter(value.properties.typeProperties.artifactId, template, linkedServices, referencedLinkedServiceSet);
                }
            } else if (__IS_FABRIC_DPE__&& propertyName === 'externalReferences' && value.connection != null) {
                value.connection = this.generateParameter(value.connection, template, linkedServices, referencedLinkedServiceSet);
            } else {
                this.parameterizeLinkedServiceReference(contextData, value, template, linkedServices, referencedLinkedServiceSet);
            }
        }
    }

    private static async _getUserParameterDefinitionJson(contextData: IResourceContext): Promise<string> {
        const metadataModeService = await getServiceInstance<MetadataModeService>(contextData, 'MetadataModeService');
        const loggerService = await getServiceInstance<LoggerService>(contextData, 'LoggerService');
        let definition: string;
        if (!metadataModeService.isLiveMode()) {
            const clientService = (await getServiceInstance<BaseFileClientProviderService<IFileClient>>(contextData, 'BaseFileClientProviderService')).client;
            try {
                definition = await clientService.getFileContent(this.armTemplateParametersFilePath);
                loggerService.log(this.className, '_getUserParameterDefinitionJson - Loaded custom parameter definition file');
            } catch (error) {
                loggerService.warn(this.className, `_getUserParameterDefinitionJson - Unable to load custom param file from repo, will use default file. Error: ${convertObjectToString(error)}`);
            }
        }
        return definition;
    }

    private static async _getDefaultParameterDefinitionJson(): Promise<string> {
        return __IS_A365__ ? JSON.stringify(A365DefaultTemplate) : JSON.stringify(AdfDefaultTemplate);
    }

    private static generateParameter(lsName: string, template: IArmTemplate, linkedServices: LinkedService[], referencedLinkedServiceSet: Set<LinkedService>): string {
        const ls = __IS_FABRIC_DPE__? linkedServices.find((l) => l.objectId === lsName) : linkedServices.find((l) => l.name === lsName);

        if (ls != null) {
            const paramName = ls.name;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (template.parameters[paramName] as any) = {
                type: 'string'
            };
            referencedLinkedServiceSet.add(ls);

            return `[${format(parameterSyntax, paramName)}]`;
        }
        return lsName;
    }
}
